import type { ArtifactType } from '@vizit/api';
import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { InfoCircleIcon } from '@vizit/icons';
import { ImageLegend, ImageLegendVariant } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import { ClearableSelect } from '../clearable-select';

type Heatmap = 'original' | ArtifactType;

const HEATMAPS: { id: Heatmap; label: string }[] = [
  { id: 'original', label: 'Original' },
  { id: 'attention', label: 'Attention map' },
  { id: 'gaze', label: 'Gaze sequence' },
  { id: 'focal_points', label: 'Focal points' },
  { id: 'drivers', label: 'Appeal drivers' },
  { id: 'detractors', label: 'Appeal detractors' },
];

const HEATMAP_ARTIFACT_TYPES: ArtifactType[] = ['attention', 'gaze', 'focal_points', 'drivers', 'detractors'];

const HEATMAP_EDUCATION: Record<
  ArtifactType,
  { title: string; description: string; legend: ImageLegendVariant; legendLabel?: string }
> = {
  attention: {
    title: 'Attention map',
    description: 'Attention maps reveal what viewers will notice most and least in your content.',
    legend: ImageLegendVariant.AttentionMap,
  },
  gaze: {
    title: 'Gaze sequence',
    description: 'Gaze sequences reveal where a viewer’s gaze is directed when looking at your content.',
    legend: ImageLegendVariant.GazeSequence,
    legendLabel: 'Gaze sequence points',
  },
  focal_points: {
    title: 'Focal points',
    description: 'Focal points determine which content elements have the highest probability of standing out.',
    legend: ImageLegendVariant.FocalPoints,
    legendLabel: 'Focal points',
  },
  drivers: {
    title: 'Visual appeal drivers',
    description: 'Visual appeal drivers show which elements are most appealing to your audience.',
    legend: ImageLegendVariant.AttentionDrivers,
  },
  detractors: {
    title: 'Visual appeal detractors',
    description: 'Visual appeal detractors show which elements are least appealing to your audience.',
    legend: ImageLegendVariant.AttentionDetractors,
  },
};

function isHeatmap(value: unknown): value is Heatmap {
  return HEATMAPS.some((heatmap) => heatmap.id === value);
}

function heatmapLabel(heatmap: Heatmap): string {
  return HEATMAPS.find((item) => item.id === heatmap)?.label ?? 'Original';
}

interface HeatmapPickerProps {
  heatmap: Heatmap;
  onHeatmapChange: (heatmap: Heatmap) => void;
  className?: string;
}

function HeatmapPicker({ heatmap, onHeatmapChange, className }: HeatmapPickerProps) {
  return (
    <ClearableSelect
      ariaLabel="Heat maps"
      placeholder="Original"
      items={HEATMAPS}
      selectedKey={heatmap}
      onSelectionChange={(key) => isHeatmap(key) && onHeatmapChange(key)}
      includeAllOption={false}
      isClearable={false}
      className={cx('min-w-[200px]', className)}
      data-testid="heatmap-picker"
    />
  );
}

interface HeatmapEducationStripProps {
  heatmap: Heatmap;
  className?: string;
}

function HeatmapEducationStrip({ heatmap, className }: HeatmapEducationStripProps) {
  if (heatmap === 'original') {
    return null;
  }
  const { title, description, legend, legendLabel } = HEATMAP_EDUCATION[heatmap];
  return (
    <section
      aria-label={`About the ${title.toLowerCase()}`}
      className={cx('flex flex-col bg-secondary md:flex-row md:items-stretch', className)}
    >
      <div className="flex flex-1 items-start gap-5 px-8 py-6 md:min-w-[240px]">
        <InfoCircleIcon size={16} className="mt-1 shrink-0 text-quaternary" aria-hidden />
        <Text size={TextSize.Sm} color={TextColor.Secondary} showEllipsis={false}>
          <Text
            elementType="span"
            size={TextSize.Sm}
            weight={TextWeight.Semibold}
            color={TextColor.Inherit}
            showEllipsis={false}
          >
            {title}
          </Text>
          {` — ${description}`}
        </Text>
      </div>
      <div
        className="mx-8 shrink-0 border-t border-secondary md:mx-0 md:my-5 md:border-t-0 md:border-l"
        aria-hidden="true"
      />
      <div className="flex shrink-0 items-center overflow-x-auto px-8 py-3 md:px-10">
        <ImageLegend
          variant={legend}
          leftLabel={legendLabel}
          className="h-16 w-auto gap-6 border-0 bg-transparent px-0"
        />
      </div>
    </section>
  );
}

export { HEATMAPS, HEATMAP_ARTIFACT_TYPES, HeatmapEducationStrip, HeatmapPicker, heatmapLabel, isHeatmap };
export type { Heatmap, HeatmapEducationStripProps, HeatmapPickerProps };
